/**
 * SendFormCard — texted forms for one contact, on the detail right rail.
 * Three jobs (mirrors CallbacksCard's booking-link flow):
 *   1. Draft the patient's /f/<token> form-link SMS into the docked composer:
 *      POST /contacts/:id/form-links { form_id, send:false } mints (or reuses)
 *      the link and returns the rendered text — the recruiter edits it and
 *      sends on the normal gated /messages path. Nothing leaves the card.
 *   2. Copy the link instead for channels we don't automate yet.
 *   3. Show prior links with live status (sent → started → completed) and the
 *      completed answers inline (label-resolved, recruiter-only).
 *
 * The picker opens on the questionnaire for the study this patient is being
 * considered for — see lib.defaultFormId.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ClipboardList, Copy, Loader2, Send } from 'lucide-react';
import api from '../../services/api';
import { Contact, ContactFormLink, CrmForm, FormResponseDetail } from '../types';
import { defaultFormId, relTime } from '../lib';
import { answerHighlight, reasonKinds } from './shared';
import { Alert } from './ui';

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

function blockedReason(reason?: string): string {
  switch (reason) {
    case 'opted_out': return 'contact has opted out of SMS';
    case 'consent_revoked': return 'SMS consent was revoked';
    case 'quiet_hours': return 'outside allowed messaging hours';
    case 'no_phone': return 'contact has no phone number';
    default: return reason || 'blocked';
  }
}

function statusPill(status: string): string {
  switch (status) {
    case 'completed': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
    case 'started': return 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300';
    case 'expired':
    case 'cancelled': return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
    default: return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
  }
}

const eligibilityLabel: Record<string, string> = {
  eligible: 'Looks eligible',
  not_eligible: 'Not eligible',
  needs_review: 'Needs review',
};

interface Props {
  contact: Contact;
  /** The study this lead is being CONSIDERED for — the effective primary the
   *  detail endpoint resolves (an explicit pin, else the study they came in
   *  with), same value Call Mode opens its study select on. Omit and the card
   *  falls back to the raw pin on the contact. */
  primaryStudyId?: string | null;
  /** Bump to refetch (e.g. after the timeline reloads). */
  reloadToken?: number;
  /** Drop the rendered form-link SMS into the docked composer (editable). */
  prefillComposer?: (text: string) => void;
  /** Rendered inside a rail disclosure row, which owns the chrome: drop this
   *  card's own border/background/shadow. Absent = the standalone card, byte
   *  for byte what the lead page renders today. */
  frameless?: boolean;
}

export default function SendFormCard({ contact, primaryStudyId: consideringStudyId, reloadToken = 0, prefillComposer, frameless = false }: Props) {
  const contactId = contact.id;
  const [links, setLinks] = useState<ContactFormLink[]>([]);
  const [forms, setForms] = useState<CrmForm[]>([]);
  const [formId, setFormId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [copying, setCopying] = useState(false);
  const [openResponse, setOpenResponse] = useState<string | null>(null); // response id
  const [responseDetail, setResponseDetail] = useState<FormResponseDetail | null>(null);
  const [responseLoading, setResponseLoading] = useState(false);
  // Guards a slow fetch from rendering one response's answers under another's
  // header after the recruiter clicks a second "View answers".
  const openResponseRef = useRef<string | null>(null);
  // Which contact/primary-study the current pick was defaulted for. A plain
  // refetch (reloadToken) must keep a recruiter's manual choice, but moving to
  // another patient — or pinning a different primary study on this one — has to
  // re-default, or the picker would still be aimed at the previous study.
  const pickedForRef = useRef<string>('');
  const primaryStudyId = consideringStudyId || contact.primary_study_id || null;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [linksRes, formsRes] = await Promise.all([
        api.get(`/api/crm/contacts/${contactId}/form-links`),
        api.get('/api/crm/forms', { params: { status: 'active' } }),
      ]);
      setLinks(linksRes.data?.links || []);
      const active = (formsRes.data?.forms || []).filter((f: CrmForm) => f.version > 0);
      setForms(active);
      const pickKey = `${contactId}:${primaryStudyId || ''}`;
      const keepPrev = pickedForRef.current === pickKey;
      setFormId((prev) => (
        keepPrev && prev && active.some((f: CrmForm) => f.id === prev)
          ? prev
          : defaultFormId(active, primaryStudyId)
      ));
      pickedForRef.current = pickKey;
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [contactId, primaryStudyId]);

  useEffect(() => { load(); }, [load, reloadToken]);

  // Draft the rendered form-link SMS into the docked composer instead of
  // direct-sending it (the booking-link precedent): the recruiter edits the
  // wording and sends via the gated /messages path, so opt-out / consent /
  // quiet-hours / char-limit and the outreach-attempt accounting all still run
  // server-side on that send — as does the link's created -> sent flip.
  const draftLink = async () => {
    if (sending || !formId) return;
    setSending(true);
    setActionError(null);
    setNotice(null);
    try {
      const res = await api.post(`/api/crm/contacts/${contactId}/form-links`, { form_id: formId, send: false });
      const body: string | undefined = res.data?.body;
      if (body && prefillComposer) {
        prefillComposer(body);
        setNotice('Draft added to the message box — edit and send from there.');
      } else {
        setNotice(res.data?.url ? `Link: ${res.data.url}` : 'Form link created.');
      }
      await load();
    } catch (e: any) {
      const d = e?.response?.data || {};
      if (e?.response?.status === 409 && d.reason) {
        setActionError(`Form link not created: ${blockedReason(d.reason)}.`);
        await load();
      } else {
        const hint = d.hint;
        setActionError(hint ? `${errMsg(e)} — ${hint}` : errMsg(e));
      }
    } finally {
      setSending(false);
    }
  };

  const copyLink = async () => {
    if (copying || !formId) return;
    setCopying(true);
    setActionError(null);
    setNotice(null);
    try {
      const res = await api.post(`/api/crm/contacts/${contactId}/form-links`, { form_id: formId, send: false });
      const url = res.data?.url;
      // Clipboard can reject (permissions, non-secure context) — the link was
      // still minted, so always surface the URL and refresh the list.
      let copied = false;
      if (url && navigator.clipboard) {
        try {
          await navigator.clipboard.writeText(url);
          copied = true;
        } catch { /* fall through to showing the URL */ }
      }
      setNotice(copied ? 'Form link copied to clipboard.' : url ? `Link: ${url}` : 'Form link created.');
      await load();
    } catch (e: any) {
      setActionError(errMsg(e));
    } finally {
      setCopying(false);
    }
  };

  const toggleResponse = async (responseId: string) => {
    if (openResponse === responseId) {
      setOpenResponse(null);
      openResponseRef.current = null;
      setResponseDetail(null);
      return;
    }
    setOpenResponse(responseId);
    openResponseRef.current = responseId;
    setResponseDetail(null);
    setResponseLoading(true);
    try {
      const res = await api.get(`/api/crm/form-responses/${responseId}`);
      if (openResponseRef.current !== responseId) return; // selection moved on
      setResponseDetail(res.data?.response || null);
    } catch (e: any) {
      if (openResponseRef.current !== responseId) return;
      setActionError(errMsg(e));
      setOpenResponse(null);
      openResponseRef.current = null;
    } finally {
      if (openResponseRef.current === responseId) setResponseLoading(false);
    }
  };

  return (
    <div
      className={frameless
        ? ''
        : 'rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-6'}
    >
      <h3 className="mb-3 inline-flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
        <ClipboardList className="h-4 w-4 text-primary-600 dark:text-primary-400" />
        Forms
      </h3>

      {notice && (
        <Alert tone="info" size="sm" icon={false} className="mb-3">{notice}</Alert>
      )}
      {(error || actionError) && (
        <Alert tone="error" size="sm" icon={false} className="mb-3">{error || actionError}</Alert>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
        </div>
      ) : (
        <>
          {forms.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No published forms yet — build one under Forms in the sidebar.
            </p>
          ) : (
            <div className="space-y-2">
              <select
                value={formId}
                onChange={(e) => setFormId(e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                aria-label="Form to send"
              >
                {/* Mark the primary study's own forms so the pre-selected row
                    reads as deliberate rather than arbitrary. */}
                {forms.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.title}{primaryStudyId && f.study_id === primaryStudyId ? ' · their study' : ''}
                  </option>
                ))}
              </select>
              <div className="flex gap-2">
                <button
                  onClick={draftLink}
                  disabled={sending || copying || !formId || !contact.phone_e164 || contact.sms_opt_out}
                  title={contact.sms_opt_out ? 'Patient opted out of SMS'
                    : !contact.phone_e164 ? 'No phone number on file'
                    : 'Draft the form-link text in the message box — you edit and send it'}
                  className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-primary-600 px-3 py-2 text-xs font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50 min-h-[44px] sm:min-h-0"
                >
                  {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                  {sending ? 'Drafting…' : 'Draft form text'}
                </button>
                <button
                  onClick={copyLink}
                  disabled={sending || copying || !formId}
                  title="Create the link and copy it to the clipboard"
                  className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-xs font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 min-h-[44px] sm:min-h-0"
                >
                  {copying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Copy className="h-3.5 w-3.5" />}
                  Copy
                </button>
              </div>
            </div>
          )}

          {links.length > 0 && (
            <div className="mt-4 divide-y divide-gray-100 dark:divide-gray-700">
              {links.map((l) => (
                <div key={l.id} className="py-2.5">
                  <div className="flex items-center justify-between gap-3">
                    <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{l.form_title}</span>
                    <span className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium ${statusPill(l.status)}`}>
                      {l.status}
                    </span>
                  </div>
                  <p className="mt-0.5 text-xs text-gray-400">
                    {l.status === 'completed' && l.response?.completed_at
                      ? `Completed ${relTime(l.response.completed_at)}`
                      : l.sent_at
                      ? `Sent ${relTime(l.sent_at)}`
                      : `Created ${relTime(l.created_at)}`}
                    {l.response?.eligibility_status && (
                      <span className="ml-1.5 font-medium text-gray-500 dark:text-gray-300">
                        · {eligibilityLabel[l.response.eligibility_status] || l.response.eligibility_status}
                      </span>
                    )}
                  </p>
                  {l.response?.status === 'completed' && (
                    <button
                      onClick={() => toggleResponse(l.response!.id)}
                      className="mt-1 text-xs font-medium text-primary-700 hover:underline dark:text-primary-400"
                    >
                      {openResponse === l.response.id ? 'Hide answers' : 'View answers'}
                    </button>
                  )}
                  {openResponse === l.response?.id && (
                    <div className="mt-2 rounded-lg bg-gray-50 p-3 dark:bg-gray-900/50">
                      {responseLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                      ) : responseDetail ? (
                        <dl className="space-y-2">
                          {/* Red = met an exclusion rule, yellow = met a needs-review
                              rule (the per-answer verdict the server resolves, stored
                              eligibility_reasons as the older-server fallback) — same
                              treatment as FormResponseModal / ScreenerPanel. */}
                          {(() => {
                            const kinds = reasonKinds(responseDetail.eligibility_reasons);
                            return responseDetail.answers.map((a) => {
                              const kind = a.eligibility_kind ?? kinds.get(a.question_key);
                              return (
                                <div key={a.question_key}>
                                  <dt className="text-xs text-gray-500 dark:text-gray-400">{a.label}</dt>
                                  <dd className="text-sm">
                                    <span
                                      className={`break-words ${answerHighlight(kind)}`}
                                      title={kind === 'exclude' ? 'Meets exclusion criteria' : kind === 'review' ? 'Needs review' : undefined}
                                    >
                                      {a.display}
                                    </span>
                                  </dd>
                                </div>
                              );
                            });
                          })()}
                          {responseDetail.answers.length === 0 && (
                            <p className="text-xs text-gray-400">No answers recorded.</p>
                          )}
                        </dl>
                      ) : (
                        <p className="text-xs text-gray-400">Could not load answers.</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
